import { useInput } from "@redotech/react-util/form";
import { useHandler } from "@redotech/react-util/hook";
import {
  FulfillmentOrderAddress,
  VerifiedAddress,
} from "@redotech/redo-fulfillment-customs-common/fulfillment-order-address";
import {
  RedoShopifyCountryCode,
  tryCoalesceStringToRedoShopifyCountryCode,
} from "@redotech/redo-inventory-common/fulfillment-country-code";
import { createAddressAutocompleteForm } from "@redotech/redo-web/address-autocomplete-form";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { memo, useEffect } from "react";
import { AddressForm } from "./address-form";
import { normalizeAddressProvince } from "./address-province";

const changeShippingAddressForm = createAddressAutocompleteForm(false, true);

// Simplified initial form values
const emptyForm = {
  name: "",
  address1: "",
  address2: "",
  city: "",
  province: "",
  zip: "",
  countryCode: RedoShopifyCountryCode.Us,
  phone: "888-888-8888",
  company: null,
  email: null,
};

export const ChangeShippingAddressModal = memo(
  function ChangeShippingAddressModal({
    onSubmit,
    address,
    open,
    onClose,
  }: {
    onSubmit: (address: FulfillmentOrderAddress) => void;
    address: FulfillmentOrderAddress | VerifiedAddress | null | undefined;
    open: boolean;
    onClose: () => void;
  }) {
    const addressInput = useInput(changeShippingAddressForm, emptyForm);

    useEffect(() => {
      const countryCode =
        tryCoalesceStringToRedoShopifyCountryCode(address?.countryCode ?? "") ??
        RedoShopifyCountryCode.Us;
      addressInput.setValue({
        name: address?.name || "",
        address1: address?.address1 || "",
        address2: address?.address2 || "",
        city: address?.city || "",
        province: normalizeAddressProvince(
          address?.province ?? "",
          countryCode,
        ),
        zip: address?.zip || "",
        countryCode,
        phone: address?.phone || emptyForm.phone,
        company: address?.company || null,
        email: address?.email || null,
      });
      // FIXME
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [address]);

    const handleSave = useHandler(() => {
      const value = addressInput.value;
      const newAddress: FulfillmentOrderAddress = {
        name: value.name,
        address1: value.address1,
        address2: value.address2 || "",
        city: value.city,
        province: value.province,
        zip: value.zip,
        phone: value.phone || "",
        countryCode: value.countryCode,
        email: value.email || "",
        company: value.company || "",
      };
      onSubmit(newAddress);
    });

    return (
      <RedoModal
        isOpen={open}
        modalSize="sm"
        onModalCloseRequested={onClose}
        primaryButton={{
          text: "Save",
          onClick: handleSave,
          disabled: addressInput.allErrors.length > 0,
        }}
        secondaryButton={{ text: "Cancel", onClick: onClose }}
        title="Change shipping address"
      >
        <Flex dir="column" gap="xl">
          <AddressForm form={addressInput} requirePhone title="" />
        </Flex>
      </RedoModal>
    );
  },
);
